<template>
  <ResponseTile
    v-if="showErrorTile"
    :back-button-label="$t('changeBankDataList.response.buttonLabel')"
    :text="$t('changeBankDataList.response.serviceErrorText')"
    :title="$t('changeBankDataList.response.serviceErrorTitle')"
    @button:back="formAbortActionToPersonalProfil"
  ></ResponseTile>
  <div v-else>
    <Spinner v-if="formLoading" class="fix-spinner"></Spinner>
    <div v-else>
      <SelfServiceTile
        :apiRequestConfig="apiRequestConfig"
        :changeRequestType="changeRequestType()"
        :checkPoliciesDetails="checkPoliciesDetails"
        :checkPolicyDetails="checkPolicyDetails"
        :checkPolicyOverview="checkPolicyOverview"
        :formAbortAction="formAbortActionToPersonalProfil"
        :formLoading="formLoading"
        :formShowResponse="formShowResponse"
        :formSubmitDisabled="formSubmitDisabled"
        :formSubmitting="formSubmitting"
        :getFormBlocks="getFormBlocks"
        :getListBlocks="getListBlocks"
        :modifyPolicy="modifyPolicy"
        :preSelectedPolicyIds="preSelectedPolicyIds"
        :selfServiceLabels="selfServiceLabels"
        :setSelectedPolicies="setSelectedPolicies"
        :staticPoliciesWithIconAndTitles="staticPoliciesFiltered"
        @formInputStartDate="setFormInputStartDate"
        @formSubmit="formSubmit"
      >
        <template #change-component>
          <ChangeBankDataForm
            ref="form"
            :formStartDate="formStartDate"
            :policies="selectedPolicies"
            @formLoading="setFormLoading"
            @formResetButton="resetFormButton"
            @formSubmitDisabled="setFormSubmitDisabled"
            @showErrorTile="setShowErrorTile"
          ></ChangeBankDataForm>
        </template>
      </SelfServiceTile>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, type Ref, ref } from 'vue'
import { type Policy } from 'policies-api'
import {
  type KeyValueType,
  ResponseTile,
  type SelfServiceLabels,
  SelfServiceTile,
  Spinner
} from 'de-zurich-ui-library'
import { useI18n } from 'vue-i18n'
import ChangeBankDataForm from '@/components/policy/self-services/ChangeBankDataForm.vue'
import { PolicyList } from '@/utils/policy/PolicyList'
import { selfServiceListOnMounted } from '@/utils/policy/PolicyUtils'
import { usePoliciesStore } from '@/stores/policiesStore'
import {
  formAbortActionToPersonalProfil,
  useSelfServicePolicyList
} from '@/composables/useSelfServicePolicyList'

const { t } = useI18n()

const policiesStore = usePoliciesStore()

const {
  staticPoliciesFiltered,
  formLoading,
  showErrorTile,
  selectedPolicies,
  loadPolicies,
  setFormLoading,
  setShowErrorTile,
  setSelectedPolicies,
  modifyPolicy,
  checkPolicyOverview,
  checkPolicyDetails,
  checkPoliciesDetails,
  apiRequestConfig
} = useSelfServicePolicyList(selfServiceListOnMounted)

// authorization and redirects
const form = ref<typeof ChangeBankDataForm | null>(null)
const formSubmitting = ref(false)
const formSubmitDisabled = ref(true)
const formShowResponse = ref(false)
const formStartDate = ref('')
const selfServiceLabels: SelfServiceLabels = {
  listHeadline: t('changeBankDataList.list.listHeadline').toString(),
  errorTitle: t('changeBankDataList.error.errorTitle').toString(),
  errorText: t('changeBankDataList.error.errorText').toString(),
  formCardTitle: t('changeBankDataList.form.formCardTitle').toString(),
  formCurrentDataLabel: t('changeBankDataList.form.formCurrentBankData').toString(),
  formCurrentDataValue: '',
  formDateLabel: t('changeBankDataList.form.formInputLabelDate').toString(),
  formDateErrorText: t('changeBankDataList.form.formDateErrorText').toString(),
  responseTitle: t('changeBankDataList.response.thankYouTitle').toString(),
  responseText: t('changeBankDataList.response.thankYouText').toString(),
  responseTileBackButtonLabel: t('changeBankDataList.response.buttonLabel').toString()
}
const preSelectedPolicyIds: Ref<string[]> = ref([])

enum ChangeRequestType {
  BankData = 'BankData'
}

onMounted(async () => {
  // Preselection: consume all policy ids once for the self-service list
  preSelectedPolicyIds.value = policiesStore.consumePreSelectedPolicyIds()

  const filtered = await loadPolicies()
  if (filtered.length === 0) {
    setShowErrorTile(true)
  }

  setFormLoading(false)
})

function changeRequestType(): ChangeRequestType {
  return ChangeRequestType.BankData
}

// Default Methods

function setFormSubmitDisabled(value: boolean): void {
  formSubmitDisabled.value = value
}

function setFormInputStartDate(value: string): void {
  formStartDate.value = value
}

function resetFormButton(): void {
  formSubmitting.value = false
}

function formSubmit(): void {
  formSubmitting.value = true
  form.value?.formSubmit()
}

function getListBlocks(policy: Policy): KeyValueType[] {
  return PolicyList.getListBlocks(policy, 'changeBankDataList')
}

function getFormBlocks(policies: Policy[]): KeyValueType[][] {
  return PolicyList.getFormBlocks(policies, 'changeBankDataList')
}
</script>

<style lang="scss" scoped>
.fix-spinner {
  position: relative !important;
  top: 64px !important;
}
</style>
